import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  GetWoundByIdResponse,
  WoundAnalysisMetrics,
} from '../../../core/models/wound/wound.model';
import { ModelViewerComponent } from '../../../main/components/model-viewer/model-viewer.component';

@Component({
  selector: 'app-wound-analysis-metrics',
  standalone: true,
  imports: [CommonModule, DecimalPipe, TranslateModule, ModelViewerComponent],
  templateUrl: './wound-analysis-metrics.component.html',
  styleUrl: './wound-analysis-metrics.component.scss',
})
export class WoundAnalysisMetricsComponent implements OnInit, OnDestroy {
  @Input() woundId!: string;
  @Input() wound: GetWoundByIdResponse | null = null;

  private destroy$ = new Subject<void>();

  metrics: WoundAnalysisMetrics | null = null;
  activeModelTab: 'painted' | 'overlay' | 'patch' = 'painted';
  isLoadingMetrics = false;

  get activeModelUrl(): string | null | undefined {
    switch (this.activeModelTab) {
      case 'painted': return this.wound?.paintedModelPath;
      case 'overlay': return this.wound?.overlayModelPath;
      case 'patch':   return this.wound?.woundPatchPath;
    }
  }

  get hasModels(): boolean {
    return !!(this.wound?.paintedModelPath || this.wound?.overlayModelPath || this.wound?.woundPatchPath);
  }

  ngOnInit(): void {
    if (this.wound?.metricsPath) {
      this.isLoadingMetrics = true;
      this.http
        .get<Record<string, unknown>>(this.wound.metricsPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.parseMetrics(JSON.stringify(data));
            this.isLoadingMetrics = false;
          },
          error: () => {
            this.isLoadingMetrics = false;
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    private http: HttpClient,
  ) {}

  get hasResults(): boolean {
    return this.metrics !== null || this.hasModels;
  }

  get boundingBoxLabel(): string {
    const bb = this.metrics?.boundingBoxMm;
    if (!bb || bb.length < 3) return '—';
    const c = (mm: number) => (mm / 10).toFixed(2);
    return `${c(bb[0])} × ${c(bb[1])} × ${c(bb[2])} cm`;
  }

  private parseMetrics(json: string): void {
    try {
      const raw = JSON.parse(json);
      this.metrics = {
        surfaceAreaMm2: raw.surface_area_mm2 ?? raw.surfaceAreaMm2 ?? 0,
        surfaceAreaCm2: raw.surface_area_cm2 ?? raw.surfaceAreaCm2 ?? 0,
        volumeMm3: raw.volume_mm3 ?? raw.volumeMm3 ?? 0,
        volumeCm3: raw.volume_cm3 ?? raw.volumeCm3 ?? 0,
        volumeIsApproximate: raw.volume_is_approximate ?? raw.volumeIsApproximate ?? false,
        isWatertight: raw.is_watertight ?? raw.isWatertight ?? false,
        boundingBoxMm: raw.bounding_box_mm ?? raw.boundingBoxMm,
        centroidMm: raw.centroid_mm ?? raw.centroidMm,
        boundaryLengthMm: raw.boundary_length_mm ?? raw.boundaryLengthMm ?? 0,
        numVertices: raw.num_vertices ?? raw.numVertices ?? 0,
        numFaces: raw.num_faces ?? raw.numFaces ?? 0,
      };
    } catch {
      // JSON parse failed silently
    }
  }

}
